import React from "react";
import {
  useGame,
  usePlayer,
  usePlayers,
} from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import { TestContinueWithoutInteraction } from "../components/TestContinueWithoutInteraction";
import { DonutChart } from "../negotiation/NegotiationDonutCharts";
import {
  LossFrameMoneyKeptBarCard,
  LossFrameStage1MoneyKeptCard,
} from "../stage2/Stage2ResultStage";
import {
  calculateTotalPay,
  DEFAULT_COMPENSATION_CONFIG,
  roundDollarsToCents,
} from "../../../shared/compensation.js";
import { getCounterpartCopy, isLossFrameDesign } from "../payoffFrame";
import { showsPerformanceFeedback } from "../../../shared/experimentBehavior.js";

function dollars(value) {
  return `$${(roundDollarsToCents(value) ?? 0).toFixed(2)}`;
}

function seconds(value) {
  return `${Number(value || 0).toFixed(1)}s`;
}

function percent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function AmountCard({ label, value, colorClassName = "text-gray-900" }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white p-4">
      <div className="text-2xl text-gray-900">{label}</div>
      <div className={`mt-1 text-3xl font-semibold ${colorClassName}`}>
        {dollars(value)}
      </div>
    </div>
  );
}

function ContributionCard({
  title,
  dollarsValue,
  secondsValue,
  percentValue,
  colorClassName,
  barClassName,
}) {
  const safePercent = Math.max(0, Math.min(100, Number(percentValue) || 0));

  return (
    <div className="rounded-md border border-gray-200 bg-white p-4 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-2xl text-gray-900">{title}</div>
          <div className={`mt-1 text-3xl font-semibold ${colorClassName}`}>
            {dollars(dollarsValue)}
          </div>
        </div>
        <div className="text-right text-2xl text-gray-700">
          <div>{seconds(secondsValue)}</div>
          <div className="font-semibold text-gray-900">{percent(percentValue)}</div>
        </div>
      </div>
      <div className="h-4 overflow-hidden rounded bg-gray-100">
        <div
          className={`h-full rounded ${barClassName}`}
          style={{ width: `${safePercent}%` }}
        />
      </div>
    </div>
  );
}

function SectionHeader({ title, children }) {
  return (
    <div>
      <h3 className="text-3xl font-semibold text-gray-900">{title}</h3>
      {children ? <p className="mt-1 text-2xl text-gray-600">{children}</p> : null}
    </div>
  );
}

export function OutcomeStage({ next, back = null }) {
  const game = useGame();
  const player = usePlayer();
  const players = usePlayers();
  const outcome = game.get("outcome");
  const stage1 = game.get("stage1");
  const design = game.get("design") || {};
  const lossFrame = isLossFrameDesign(design);
  const counterpart = getCounterpartCopy(design);

  if (!outcome) {
    return (
      <div className="max-w-3xl mx-auto px-8 py-6 text-center text-2xl text-gray-500">
        Computing final outcome...
      </div>
    );
  }

  const mine = outcome.playerOutcomes?.[player.id] || {};
  const partner = players.find((p) => p.id !== player.id);
  const partnerOutcome = partner
    ? outcome.playerOutcomes?.[partner.id]
    : null;
  const stage1Data = stage1 || outcome.stage1 || {};
  const stage2 = outcome.stage2 || {};
  const stage1TeamInCircleSeconds = Number(stage1Data.teamInCircleSeconds ?? 0);
  const stage1TeamWinnings = Number(stage1Data.teamWinnings ?? 0);
  const stage2TeamInCircleSeconds = Number(stage2.teamInCircleSeconds ?? 0);
  const stage2TeamWinningsBeforeAdjustment = Number(stage2.teamWinningsBeforeAdjustment ?? 0);
  const stage2TeamWinnings = Number(stage2.teamWinnings ?? 0);
  const stage1DurationSeconds = Number(stage1Data.durationSeconds ?? stage1TeamInCircleSeconds);
  const stage2DurationSeconds = Number(stage2.durationSeconds ?? stage2TeamInCircleSeconds);
  const stage1Earnings = Number(mine.stage1Earnings ?? 0);
  const stage2Earnings = Number(mine.stage2Earnings ?? 0);
  const partnerStage1Earnings = Number(partnerOutcome?.stage1Earnings ?? 0);
  const partnerStage2Earnings = Number(partnerOutcome?.stage2Earnings ?? 0);
  const showUpPay = Number(
    design.basePay ?? design.showUpPayment ?? DEFAULT_COMPENSATION_CONFIG.basePay
  );
  const baseBonus = Number(design.baseBonus ?? DEFAULT_COMPENSATION_CONFIG.baseBonus);
  const totalPay = Number(
    mine.total ??
      calculateTotalPay({
        basePay: showUpPay,
        baseBonus,
        stage1Earnings,
        stage2Earnings,
      })
  );
  const showPerformanceFeedback = showsPerformanceFeedback(design);
  const youWonStage1 = stage1Data.winnerId
    ? stage1Data.winnerId === player.id
    : stage1Earnings > 0;
  const usedRandomSplit = Boolean(stage2.failedAgreementPenaltyApplied);

  if (!showPerformanceFeedback) {
    return (
      <div className="max-w-4xl mx-auto px-8 py-6 space-y-6">
        <div className="space-y-3">
          <h2 className="text-4xl font-semibold text-gray-900">Final Outcome</h2>
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-5 text-emerald-900">
            <div className="text-2xl font-semibold">Your total pay</div>
            <div className="mt-1 text-5xl font-bold text-emerald-950">
              {dollars(totalPay)}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <AmountCard label="Show-up pay" value={showUpPay} />
          <AmountCard label="Game 1 earnings" value={stage1Earnings} />
          <AmountCard label="Game 2 earnings" value={stage2Earnings} />
        </div>

        <section className="rounded-md border border-gray-200 bg-white p-5 space-y-3">
          <h3 className="text-3xl font-semibold text-gray-900">Game 1</h3>
          <p className="text-2xl text-gray-700">
            {youWonStage1 ? "You won Game 1." : `Your ${counterpart.term} won Game 1.`}
          </p>
        </section>

        <section className="rounded-md border border-gray-200 bg-white p-5 space-y-3">
          <h3 className="text-3xl font-semibold text-gray-900">Game 2</h3>
          <p className="text-2xl text-gray-700">
            Winnings available for negotiation:{" "}
            <strong>{dollars(stage2TeamWinningsBeforeAdjustment)}</strong>.
          </p>
          <p className="text-2xl text-gray-700">
            {usedRandomSplit
              ? "The negotiation did not clear, so the pot was reduced and split randomly."
              : "The negotiation cleared, and you received your negotiated Game 2 pay."}
          </p>
        </section>

        {typeof next === "function" ? (
          <div className="flex items-center gap-4">
            {typeof back === "function" ? (
              <Button handleClick={back} primary className="text-xl">
                Back
              </Button>
            ) : null}
            <Button handleClick={next} autoFocus className="text-xl">
              Continue
            </Button>
          </div>
        ) : null}
        <TestContinueWithoutInteraction
          onContinue={next}
          disabled={typeof next !== "function"}
        />
      </div>
    );
  }

  const stage1ScoresByPlayerId = Object.fromEntries(
    (stage1Data.scores || []).map((score) => [
      score.playerId,
      Number(score.inCircleSeconds ?? 0),
    ])
  );
  const yourStage1Seconds =
    Number(mine.stage1InCircleSeconds ?? stage1ScoresByPlayerId[player.id] ?? 0);
  const partnerStage1Seconds = Number(
    partnerOutcome?.stage1InCircleSeconds ??
      (partner ? stage1ScoresByPlayerId[partner.id] : 0) ??
      0
  );
  const yourStage2Seconds = Number(mine.stage2InCircleSeconds ?? 0);
  const partnerStage2Seconds = Number(partnerOutcome?.stage2InCircleSeconds ?? 0);
  const yourStage1Percent =
    stage1DurationSeconds > 0 ? (yourStage1Seconds / stage1DurationSeconds) * 100 : 0;
  const partnerStage1Percent =
    stage1DurationSeconds > 0 ? (partnerStage1Seconds / stage1DurationSeconds) * 100 : 0;
  const yourStage2Percent =
    stage2DurationSeconds > 0 ? (yourStage2Seconds / stage2DurationSeconds) * 100 : 0;
  const partnerStage2Percent =
    stage2DurationSeconds > 0 ? (partnerStage2Seconds / stage2DurationSeconds) * 100 : 0;
  const stage1TrackingDollarsPerSecond = Math.max(
    0,
    Number(stage1Data.trackingDollarsPerSecond ?? design.trackingDollarsPerSecond ?? 0)
  );
  const configuredStage1StakeDollars = Number(
    stage1Data.stage1MaxPerParticipant ?? design.stage1MaxPerParticipant
  );
  const stage1StakeDollars = Math.max(
    0,
    Number.isFinite(configuredStage1StakeDollars)
      ? configuredStage1StakeDollars
      : stage1DurationSeconds * stage1TrackingDollarsPerSecond
  );
  const yourStage1ContributionDollars =
    stage1TeamInCircleSeconds > 0
      ? stage1TeamWinnings * (yourStage1Seconds / stage1TeamInCircleSeconds)
      : 0;
  const partnerStage1ContributionDollars =
    stage1TeamInCircleSeconds > 0
      ? stage1TeamWinnings * (partnerStage1Seconds / stage1TeamInCircleSeconds)
      : 0;
  const yourStage2ContributionDollars =
    stage2TeamInCircleSeconds > 0
      ? stage2TeamWinningsBeforeAdjustment * (yourStage2Seconds / stage2TeamInCircleSeconds)
      : 0;
  const partnerStage2ContributionDollars =
    stage2TeamInCircleSeconds > 0
      ? stage2TeamWinningsBeforeAdjustment * (partnerStage2Seconds / stage2TeamInCircleSeconds)
      : 0;
  const yourStage1SavedDollars = Math.max(
    0,
    Math.min(yourStage1Seconds * stage1TrackingDollarsPerSecond, stage1StakeDollars)
  );
  const partnerStage1SavedDollars = Math.max(
    0,
    Math.min(partnerStage1Seconds * stage1TrackingDollarsPerSecond, stage1StakeDollars)
  );
  const stage2ShrinkLoss = usedRandomSplit
    ? Math.max(0, stage2TeamWinningsBeforeAdjustment - stage2TeamWinnings)
    : 0;

  return (
    <div className="max-w-4xl mx-auto px-8 py-6 space-y-6">
      <div className="space-y-3">
        <h2 className="text-4xl font-semibold text-gray-900">Final Outcome</h2>
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-5 text-emerald-900">
          <div className="text-2xl font-semibold">Your total pay</div>
          <div className="mt-1 text-5xl font-bold text-emerald-950">
            {dollars(totalPay)}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <AmountCard label="Show-up pay" value={showUpPay} />
        <AmountCard label={lossFrame ? "Game 1 money kept" : "Game 1 earnings"} value={stage1Earnings} />
        <AmountCard label={lossFrame ? "Game 2 money kept" : "Game 2 earnings"} value={stage2Earnings} />
      </div>

      <div className="rounded-md border border-gray-200 p-5 space-y-4">
        <SectionHeader
          title="Game 1"
        >
          {lossFrame ? (
            <>
              You started with <strong>{dollars(stage1StakeDollars)}</strong>{" "}
              at stake in Game 1.
            </>
          ) : (
            <>
              Your team earned <strong>{dollars(stage1TeamWinnings)}</strong>{" "}
              from time in the circle.
            </>
          )}
        </SectionHeader>
        {lossFrame ? (
          <LossFrameStage1MoneyKeptCard
            stage1StakeDollars={stage1StakeDollars}
            yourSavedDollars={yourStage1SavedDollars}
            partnerSavedDollars={partnerStage1SavedDollars}
            youWon={youWonStage1}
            tied={stage1Data.tied === true}
            design={design}
          />
        ) : (
          <>
            <div className="grid lg:grid-cols-2 gap-4">
              <ContributionCard
                title="Your contribution"
                dollarsValue={yourStage1ContributionDollars}
                secondsValue={yourStage1Seconds}
                percentValue={yourStage1Percent}
                colorClassName="text-blue-700"
                barClassName="bg-blue-600"
              />
              <ContributionCard
                title={`${counterpart.capitalizedTerm} contribution`}
                dollarsValue={partnerStage1ContributionDollars}
                secondsValue={partnerStage1Seconds}
                percentValue={partnerStage1Percent}
                colorClassName="text-red-700"
                barClassName="bg-red-600"
              />
            </div>
            <DonutChart
              title="Game 1 Pay"
              legendSide
              chartClassName="w-56 h-56"
              centerValueClassName="fill-blue-700 text-sm font-semibold"
              centerTotalClassName="fill-gray-900 text-sm font-semibold"
              centerLabelClassName="fill-gray-500 text-[10px]"
              legendTextClassName="text-3xl"
              showZeroValueLegendItems
              centerValue={stage1Earnings}
              centerTotal={stage1Earnings + partnerStage1Earnings}
              segments={[
                { label: "Your pay", value: stage1Earnings, color: "#4f46e5" },
                { label: `${counterpart.capitalizedTerm} pay`, value: partnerStage1Earnings, color: "#dc2626" },
              ]}
            />
          </>
        )}
      </div>

      <div className="rounded-md border border-gray-200 p-5 space-y-4">
        <SectionHeader
          title="Game 2"
        >
          Your team {lossFrame ? "saved" : "earned"} <strong>{dollars(stage2TeamWinningsBeforeAdjustment)}</strong>{" "}
          {lossFrame ? "from being lost" : "from time in the circle"}.
        </SectionHeader>

        {usedRandomSplit ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-2xl text-red-900">
            {lossFrame ? "Negotiation did not allocate enough loss. The saved money shrank to " : "Negotiation did not clear. The pot shrank to "}
            <strong>{dollars(stage2TeamWinnings)}</strong> and was split randomly.
          </div>
        ) : (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-2xl text-emerald-900">
            {lossFrame
              ? "Negotiation allocated enough loss. You kept the negotiated Game 2 amount."
              : "Negotiation cleared. You received the negotiated Game 2 pay."}
          </div>
        )}

        {lossFrame ? (
          <LossFrameMoneyKeptBarCard
            title="Game 2 Money Kept"
            stage1={stage1Data}
            stage2={stage2}
            design={design}
            player={player}
            players={players}
            yourMoneyKept={stage2Earnings}
            totalDollars={stage2TeamWinningsBeforeAdjustment}
            lostToShrinkDollars={stage2ShrinkLoss}
          />
        ) : (
          <>
            <div className="grid lg:grid-cols-2 gap-4">
              <ContributionCard
                title="Your contribution"
                dollarsValue={yourStage2ContributionDollars}
                secondsValue={yourStage2Seconds}
                percentValue={yourStage2Percent}
                colorClassName="text-blue-700"
                barClassName="bg-blue-600"
              />
              <ContributionCard
                title={`${counterpart.capitalizedTerm} contribution`}
                dollarsValue={partnerStage2ContributionDollars}
                secondsValue={partnerStage2Seconds}
                percentValue={partnerStage2Percent}
                colorClassName="text-red-700"
                barClassName="bg-red-600"
              />
            </div>
            <DonutChart
              title="Game 2 Pay"
              legendSide
              chartClassName="w-56 h-56"
              centerValueClassName="fill-blue-700 text-sm font-semibold"
              centerTotalClassName="fill-gray-900 text-sm font-semibold"
              centerLabelClassName="fill-gray-500 text-[10px]"
              legendTextClassName="text-3xl"
              chartOrderLabels={
                usedRandomSplit
                  ? [
                      `${counterpart.capitalizedTerm} pay`,
                      "Lost to Shrink",
                      "Your pay",
                    ]
                  : [
                      `${counterpart.capitalizedTerm} pay`,
                      "Your pay",
                    ]
              }
              centerValue={stage2Earnings}
              centerTotal={stage2TeamWinningsBeforeAdjustment}
              segments={
                usedRandomSplit
                  ? [
                      { label: "Your pay", value: stage2Earnings, color: "#4f46e5" },
                      { label: `${counterpart.capitalizedTerm} pay`, value: partnerStage2Earnings, color: "#dc2626" },
                      { label: "Lost to Shrink", value: stage2ShrinkLoss, color: "#6b7280" },
                    ]
                  : [
                      { label: "Your pay", value: stage2Earnings, color: "#4f46e5" },
                      { label: `${counterpart.capitalizedTerm} pay`, value: partnerStage2Earnings, color: "#dc2626" },
                    ]
              }
            />
          </>
        )}
      </div>

      {typeof next === "function" ? (
        <div className="flex items-center gap-4">
          {typeof back === "function" ? (
            <Button handleClick={back} primary className="text-xl">
              Back
            </Button>
          ) : null}
          <Button handleClick={next} autoFocus className="text-xl">
            Continue
          </Button>
        </div>
      ) : null}
      <TestContinueWithoutInteraction onContinue={next} disabled={typeof next !== "function"} />
    </div>
  );
}
